import { useState } from "react";
import CouponHeroCallout from "@/components/hola/CouponHeroCallout";
import RaffleHeroCallout from "@/components/hola/RaffleHeroCallout";
import ReferralHeroCallout from "@/components/hola/ReferralHeroCallout";
import { useSettings } from "@/lib/settings";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Radar, MapPin, MessageSquare, QrCode, Sparkles, Gift, Store, BarChart3, ArrowRight, Check, Zap, Compass, Rocket, Briefcase, PawPrint, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import WinnersCarousel from "@/components/hola/WinnersCarousel";

const B2C = "b2c";
const B2B = "b2b";

export default function LandingPage() {
  const settings = useSettings();
  const [mode, setMode] = useState(B2C);
  const isB2C = mode === B2C;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-neutral-200/60">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <div className="w-9 h-9 rounded-xl bg-black text-white grid place-items-center">
              <Radar className="w-5 h-5" />
            </div>
            <span className="font-heading font-extrabold text-lg tracking-tight">HOLA RADAR</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-neutral-600">
            <a href="#how" className="hover:text-black transition-colors">Cómo funciona</a>
            <a href="#benefits" className="hover:text-black transition-colors">Beneficios</a>
            {!isB2C && (
              <a href="#pricing" className="hover:text-black transition-colors" data-testid="nav-pricing-link">Precios</a>
            )}
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="header-login-link">
              <Button variant="ghost" className="rounded-full font-semibold">Entrar</Button>
            </Link>
            <Link to="/register" data-testid="header-register-link">
              <Button className="rounded-full bg-black text-white hover:bg-neutral-800 font-semibold">Registrar comercio</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO with dual toggle */}
      <section className="relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 -z-0 dot-grid opacity-40 pointer-events-none" aria-hidden="true" />
        <div
          className={`absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full blur-3xl -z-10 transition-colors duration-700 ${
            isB2C ? "bg-[#FF007A]/25" : "bg-[#0055FF]/25"
          }`}
        />
        <div
          className={`absolute -top-10 right-0 w-[420px] h-[420px] rounded-full blur-3xl -z-10 transition-colors duration-700 ${
            isB2C ? "bg-amber-300/30" : "bg-cyan-300/30"
          }`}
        />

        <div className="max-w-7xl mx-auto px-5 md:px-10 pt-14 pb-24 md:pt-20 md:pb-32">
          {/* Toggle */}
          <div className="flex justify-center mb-10">
            <div
              className="inline-flex items-center rounded-full p-1.5 bg-white border border-neutral-200 shadow-sm"
              data-testid="hero-mode-toggle"
              role="tablist"
            >
              <button
                data-testid="toggle-b2c"
                onClick={() => setMode(B2C)}
                className={`relative px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${
                  isB2C ? "text-white" : "text-neutral-500 hover:text-black"
                }`}
              >
                {isB2C && (
                  <motion.span
                    layoutId="pill"
                    className="absolute inset-0 rounded-full bg-[#FF007A]"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Soy Usuario
                </span>
              </button>
              <button
                data-testid="toggle-b2b"
                onClick={() => setMode(B2B)}
                className={`relative px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${
                  !isB2C ? "text-white" : "text-neutral-500 hover:text-black"
                }`}
              >
                {!isB2C && (
                  <motion.span
                    layoutId="pill"
                    className="absolute inset-0 rounded-full bg-[#0055FF]"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative flex items-center gap-2">
                  <Store className="w-4 h-4" /> Tengo un Comercio
                </span>
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-14 items-center">
            {/* Left: Copy */}
            <div className="min-w-0">
              <AnimatePresence mode="wait">
                {isB2C ? (
                  <motion.div
                    key="copy-b2c"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.35 }}
                  >
                    <div className="inline-flex items-center gap-2 rounded-full bg-[#FF007A]/10 text-[#FF007A] px-3 py-1.5 text-xs font-bold uppercase tracking-wider mb-6">
                      <Zap className="w-3.5 h-3.5" /> Sin registro · Sin app
                    </div>
                    <h1
                      data-testid="hero-h1"
                      className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[0.95]"
                    >
                      Atrapa <span className="text-[#FF007A]">ofertas</span><br />
                      a 50 metros.
                    </h1>
                    <p
                      data-testid="hero-subtitle"
                      className="mt-6 text-lg text-neutral-600 leading-relaxed max-w-xl"
                    >
                      Tu camino por toda la ciudad viene con regalos. Enciende el GPS y HOLA RADAR te permite que, al pasar por fuera de cualquier negocio — en una plaza, un centro comercial o en cualquier calle o avenida — recibas en tu celular mensajes con <strong className="text-black">regalos y ofertas exclusivas</strong>. Además, conversa con una IA que te lo explica todo.
                    </p>

                    {/* Info card: cómo llegan realmente los usuarios (escaneando un QR físico) */}
                    <div
                      className="mt-6 rounded-2xl border border-[#FF007A]/20 bg-[#FF007A]/5 p-4 flex gap-3 items-start"
                      data-testid="hero-scan-instructions"
                    >
                      <div className="w-10 h-10 shrink-0 rounded-xl bg-[#FF007A] text-white grid place-items-center shadow-lg shadow-[#FF007A]/30">
                        <QrCode className="w-5 h-5" />
                      </div>
                      <div className="text-sm text-neutral-700 leading-snug">
                        <div className="font-bold text-neutral-900">¿Cómo entras a HOLA RADAR?</div>
                        Cuando veas nuestro <strong className="text-[#FF007A]">código QR</strong> en un poster, cartel, folleto o vitrina de la calle, ábrelo con la <strong>cámara de tu celular</strong>. Sin descargar app, sin registrarte. Listo.
                      </div>
                    </div>

                    {/* Aura — Guía de compras con IA */}
                    <div
                      className="mt-4 rounded-2xl border-2 border-[#FF007A]/30 bg-gradient-to-br from-[#FF007A]/5 via-white to-[#0055FF]/5 p-5 max-w-xl relative overflow-hidden"
                      data-testid="hero-aura-b2c-callout"
                    >
                      <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-[#FF007A]/10 blur-2xl pointer-events-none" />
                      <div className="flex items-center gap-2 mb-2 relative">
                        <div className="w-9 h-9 rounded-xl bg-[#FF007A] text-white grid place-items-center shrink-0">
                          <Compass className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#FF007A]">Nuevo · Guía de compras con IA</div>
                          <div className="font-heading font-extrabold text-base leading-tight">Conoce a Aura</div>
                        </div>
                      </div>
                      <p className="text-sm text-neutral-700 leading-relaxed relative">
                        Tu <strong>guía de compras personal</strong> por toda la ciudad: caminas por la calle, la avenida o la plaza y ella te acompaña. Le dices qué te interesa hoy — comida, ropa, tecnología, belleza — y te va llevando a los <strong>mejores precios y ofertas relámpago</strong> conforme pasas cerca de cada local. Cada tanto, te suelta una <strong className="text-[#FF007A]">sorpresa</strong> escondida que ni sabías que existía. 🎁
                      </p>
                      <p className="text-sm text-neutral-700 leading-relaxed relative mt-2" data-testid="hero-aura-chat-b2c">
                        💬 <strong>Chatea con Aura desde donde estés</strong>{settings.aura_voice_enabled ? " — por texto o por voz" : ""}: aunque vivas a kilómetros, pregúntale a la asistente de cualquier comercio por productos, precios y promociones. Y si quieres a una persona, el comercio te responde en vivo en el mismo chat.
                      </p>
                    </div>

                    {/* MANADA — Descuentos en grupo */}
                    <div className="mt-4 rounded-2xl border-2 border-[#0055FF]/30 bg-gradient-to-br from-[#0055FF]/5 via-white to-[#FF007A]/5 p-5 max-w-xl" data-testid="hero-manada-b2c-callout">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-9 h-9 rounded-xl bg-[#0055FF] text-white grid place-items-center shrink-0"><PawPrint className="w-5 h-5" /></div>
                        <div>
                          <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#0055FF]">MANADA · Descuentos grupales</div>
                          <div className="font-heading font-extrabold text-base leading-tight">¿Vas en grupo? Ahorras más.</div>
                        </div>
                      </div>
                      <p className="text-sm text-neutral-700 leading-relaxed">
                        Elige un comercio, dinos <strong>cuántos van</strong> (a comer, a cortarse el cabello, a hacerse manicure…). Entre más grande la manada, más descuento. Genera un código único y aplícalo al pagar. <strong className="text-[#0055FF]">Válido 24 horas</strong>.
                      </p>
                    </div>

                    {/* ATÍNALE AL PRECIO — Gamificación */}
                    <div className="mt-4 rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-50 via-white to-[#FF007A]/5 p-5 max-w-xl" data-testid="hero-atinale-b2c-callout">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-9 h-9 rounded-xl bg-amber-500 text-white grid place-items-center shrink-0"><Target className="w-5 h-5" /></div>
                        <div>
                          <div className="text-[10px] font-extrabold uppercase tracking-widest text-amber-600">Juego · Atínale al Precio</div>
                          <div className="font-heading font-extrabold text-base leading-tight">Adivina y llévatelo GRATIS 🎯</div>
                        </div>
                      </div>
                      <p className="text-sm text-neutral-700 leading-relaxed">
                        Los comercios afiliados suben la foto de una prenda o servicio. Tú tratas de adivinar el <strong>precio exacto</strong>. Si le atinas, <strong className="text-amber-600">te lo llevas gratis</strong> — pasas por tu premio con un código único que te damos.
                      </p>
                    </div>

                    {/* CUPONES + CUPÓN DE ORO */}
                    <CouponHeroCallout variant="b2c" />

                    {/* SORTEO MENSUAL de electrodomésticos */}
                    <RaffleHeroCallout variant="b2c" />

                    {/* VENTAS REFERIDAS — Trabaja desde casa */}
                    <ReferralHeroCallout variant="b2c" />

                    {/* Últimos ganadores — carousel motivacional */}
                    <div className="max-w-xl">
                      <WinnersCarousel />
                    </div>

                    <div className="mt-6 flex flex-wrap gap-3">
                      <Link to="/guide" data-testid="hero-cta-try-aura">
                        <Button className="h-14 px-8 rounded-full bg-[#FF007A] text-white hover:bg-[#e5006e] text-base font-bold shadow-lg shadow-[#FF007A]/30 hover:scale-[1.02] transition-transform">
                          <Compass className="w-4 h-4" /> Probar Aura ahora
                        </Button>
                      </Link>
                      <a href="#how">
                        <Button variant="outline" className="h-14 px-6 rounded-full border-neutral-300 font-semibold" data-testid="hero-cta-how">
                          Ver cómo funciona
                        </Button>
                      </a>
                      <Link to="/scan" data-testid="hero-cta-demo">
                        <Button variant="ghost" className="h-14 px-6 rounded-full font-semibold text-neutral-600">
                          Ver ejemplo del radar
                        </Button>
                      </Link>
                    </div>
                    <div className="mt-8 flex items-center gap-6 text-sm text-neutral-500">
                      <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> Sin datos personales</div>
                      <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> Apaga cuando quieras</div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="copy-b2b"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.35 }}
                  >
                    <div className="inline-flex items-start gap-2 rounded-2xl sm:rounded-full bg-[#0055FF]/10 text-[#0055FF] px-3 py-1.5 text-[10px] sm:text-xs font-bold uppercase tracking-wider mb-6 max-w-full">
                      <Store className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span className="break-words">Para cualquier comercio de la ciudad: en plaza, centro comercial, calle o avenida</span>
                    </div>
                    <h1
                      data-testid="hero-h1-b2b"
                      className="font-heading text-3xl sm:text-5xl lg:text-[3.75rem] font-extrabold tracking-tight leading-[1.05] break-words"
                    >
                      Deje de ver cómo miles de personas <span className="text-[#0055FF]">pasan de largo</span> frente a su vitrina.
                    </h1>
                    <p
                      data-testid="hero-subtitle-b2b"
                      className="mt-6 text-lg text-neutral-600 leading-relaxed max-w-xl"
                    >
                      Hoy pasaron más de <strong className="text-black">500 personas</strong> frente a su local — esté en una plaza, un centro comercial o en cualquier calle o avenida de la ciudad. El 95% no tiene idea de lo que usted vende. Conecte su negocio al celular de cada transeúnte que camine a menos de 50 metros: nuestra IA detecta la cercanía, envía sus ofertas al instante y los convence de entrar a comprar en ese mismo momento.
                    </p>

                    <div className="mt-5 rounded-2xl border border-[#0055FF]/20 bg-[#0055FF]/5 p-4 flex gap-3 items-start" data-testid="hero-ai-design-callout">
                      <div className="w-9 h-9 shrink-0 rounded-xl bg-[#0055FF] text-white grid place-items-center">
                        <Sparkles className="w-5 h-5" />
                      </div>
                      <div className="text-sm text-neutral-700 leading-relaxed">
                        <strong className="text-[#0055FF]">Nuestra IA convierte tu promo en un mensaje que atrae y convierte, en segundos:</strong> gancho, arte, gradientes, emojis, tipografía y botón. Solo cuéntale tu promo y ella se encarga del resto.
                      </div>
                    </div>

                    {/* Aura — Guía virtual que trae clientes al local */}
                    <div
                      className="mt-4 rounded-2xl border-2 border-[#0055FF]/30 bg-gradient-to-br from-[#0055FF]/5 via-white to-[#FF007A]/5 p-4 sm:p-5 relative overflow-hidden"
                      data-testid="hero-aura-b2b-callout"
                    >
                      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#0055FF]/10 blur-2xl pointer-events-none" />
                      <div className="flex items-start gap-3 mb-2 relative">
                        <div className="w-10 h-10 rounded-xl bg-[#0055FF] text-white grid place-items-center shrink-0">
                          <Compass className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#0055FF]">Nuevo · Vendedora virtual con IA</div>
                          <div className="font-heading font-extrabold text-base sm:text-lg leading-tight">Aura camina con el cliente… y lo lleva hasta tu puerta.</div>
                        </div>
                      </div>
                      <p className="text-sm text-neutral-700 leading-relaxed relative">
                        Cuando un usuario camina por tu zona — sea una plaza, un centro comercial o la calle donde está tu negocio — Aura le pregunta qué busca hoy y arma un recorrido a su medida. Al acercarse a <strong>tu local</strong>, ella le presenta tu tienda por {settings.aura_voice_enabled ? "voz o chat" : "chat"}: <em>qué vendes, tus productos estrella y tu oferta del día</em>. Le explica lo que tú previamente configuraste en tu dashboard, resuelve sus dudas y <strong className="text-[#0055FF]">lo invita a entrar</strong> con nombre y apellido. Tú vendes; ella hace el trabajo de un promotor 24/7 sin que muevas un dedo.
                      </p>
                      <p className="text-sm text-neutral-700 leading-relaxed relative mt-2" data-testid="hero-aura-chat-b2b">
                        💬 <strong className="text-[#0055FF]">Chat a distancia:</strong> tus clientes pueden escribirle a Aura desde su casa o desde otra ciudad; ella responde con tus productos, ofertas y cupones. Y cuando haga falta, <strong>tú tomas el chat en vivo</strong> desde tu dashboard.
                      </p>
                    </div>

                    {/* MANADA — Descuentos grupales */}
                    <div className="mt-4 rounded-2xl border-2 border-[#FF007A]/30 bg-gradient-to-br from-[#FF007A]/5 via-white to-[#0055FF]/5 p-4 sm:p-5" data-testid="hero-manada-b2b-callout">
                      <div className="flex items-start gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-[#FF007A] text-white grid place-items-center shrink-0"><PawPrint className="w-5 h-5" /></div>
                        <div>
                          <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#FF007A]">MANADA · Ventas grupales</div>
                          <div className="font-heading font-extrabold text-base sm:text-lg leading-tight">Llena tu local con grupos, sube tu ticket promedio.</div>
                        </div>
                      </div>
                      <p className="text-sm text-neutral-700 leading-relaxed">
                        Tú defines <strong>tramos de descuento por tamaño de grupo</strong> (ej. 3 pers = 10%, 6 pers = 15%, 10 pers = 20%). El cliente genera un código único desde su celular con su nombre — tu mesero lo canjea al pagar y queda registrado en tu reporte. Ideal para <strong className="text-[#FF007A]">restaurantes, cafeterías, salones y spas</strong>.
                      </p>
                    </div>

                    {/* ATÍNALE — Gamificación */}
                    <div className="mt-4 rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-50 via-white to-[#0055FF]/5 p-4 sm:p-5" data-testid="hero-atinale-b2b-callout">
                      <div className="flex items-start gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-amber-500 text-white grid place-items-center shrink-0"><Target className="w-5 h-5" /></div>
                        <div>
                          <div className="text-[10px] font-extrabold uppercase tracking-widest text-amber-600">Gamificación · Refuerza tu marca</div>
                          <div className="font-heading font-extrabold text-base sm:text-lg leading-tight">&ldquo;Atínale al Precio&rdquo; — juego que trae tráfico a tu local.</div>
                        </div>
                      </div>
                      <p className="text-sm text-neutral-700 leading-relaxed">
                        Sube la foto de una prenda o servicio con su <strong>precio exacto</strong>. Los usuarios intentan adivinar; quien atine se lleva <strong className="text-amber-600">tu producto gratis</strong> y va físicamente a tu tienda a reclamarlo (con código único). Los usuarios ven <strong>tu marca</strong> antes que el precio — memorizan tu logo, tu ubicación y tus productos jugando.
                      </p>
                    </div>

                    {/* CUPONES MARKETING + CUPÓN DE ORO */}
                    <CouponHeroCallout variant="b2b" />

                    {/* SORTEO MENSUAL — boletos por compra, premios pagados por MARKETING DE INTELIGENCIA ARTIFICIAL */}
                    <RaffleHeroCallout variant="b2b" />

                    {/* SISTEMA DE VENTAS REFERIDAS */}
                    <ReferralHeroCallout variant="b2b" />

                    <div className="mt-8 flex flex-wrap gap-3">
                      <Link to="/register" data-testid="hero-cta-register" className="w-full sm:w-auto">
                        <Button className="w-full sm:w-auto h-14 px-6 sm:px-8 rounded-full bg-[#0055FF] text-white hover:bg-[#0044cc] text-sm sm:text-base font-bold shadow-lg shadow-[#0055FF]/30 hover:scale-[1.02] transition-transform">
                          <Store className="w-5 h-5" /> Registra tu negocio
                        </Button>
                      </Link>
                      <Link to="/login" className="w-full sm:w-auto">
                        <Button variant="outline" className="w-full sm:w-auto h-14 px-6 rounded-full border-neutral-300 font-semibold" data-testid="hero-cta-login">
                          Ingresar <ArrowRight className="w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                    <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-neutral-500">
                      <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> Sin descargar apps</div>
                      <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> Configuración en 5 minutos</div>
                      <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> Cancele cuando quiera</div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Right: Visual */}
            <div className="relative flex justify-center min-w-0">
              <AnimatePresence mode="wait">
                {isB2C ? (
                  <motion.div
                    key="visual-b2c"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.4 }}
                    className="relative"
                  >
                    <PhoneMockup />
                  </motion.div>
                ) : (
                  <motion.div
                    key="visual-b2b"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.4 }}
                    className="relative"
                  >
                    <div className="relative">
                      <MapMockup />
                      {/* AI Message mockup floating on top of the map — "esto le llega al usuario" */}
                      <div className="hidden md:block absolute -bottom-10 -right-14 lg:-right-20 origin-bottom-right scale-[0.72] lg:scale-[0.78] drop-shadow-2xl">
                        <AIMessageMockup />
                      </div>
                      {/* Mobile: stack below */}
                      <div className="md:hidden mt-8 flex justify-center">
                        <div className="scale-90 origin-top">
                          <AIMessageMockup />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* B2B metrics strip — only visible in B2B mode */}
          <AnimatePresence>
            {!isB2C && (
              <motion.div
                key="b2b-metrics"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.4, delay: 0.15 }}
                className="mt-16 grid md:grid-cols-3 gap-4"
                data-testid="b2b-metrics-strip"
              >
                <MetricPill
                  value="+300%"
                  title="de Visibilidad"
                  desc="Su negocio deja de depender solo del diseño del aparador."
                  icon={BarChart3}
                />
                <MetricPill
                  value="0%"
                  title="de Desperdicio"
                  desc="Solo envía promociones a personas que ya están físicamente frente a tu negocio, en la calle o el pasillo correcto para comprar."
                  icon={MapPin}
                />
                <MetricPill
                  value="24/7"
                  title="Vendedor de IA"
                  desc="Un asistente virtual personalizado responde dudas, muestra su catálogo e invita al cliente a pasar al local."
                  icon={MessageSquare}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Modo prueba (asesores comerciales) — link discreto sólo en B2B */}
          {!isB2C && (
            <div className="mt-10 flex justify-center flex-wrap gap-2">
              <a
                href="/demo/login"
                className="text-xs text-neutral-400 hover:text-[#FF007A] transition-colors border border-neutral-200 hover:border-[#FF007A]/40 rounded-full px-4 py-1.5 inline-flex items-center gap-1.5"
                data-testid="demo-hero-link"
              >
                <Rocket className="w-3 h-3" /> Modo Prueba (asesores)
              </a>
              <a
                href="/asesor/login"
                className="text-xs text-neutral-400 hover:text-[#0055FF] transition-colors border border-neutral-200 hover:border-[#0055FF]/40 rounded-full px-4 py-1.5 inline-flex items-center gap-1.5"
                data-testid="advisor-hero-link"
              >
                <Briefcase className="w-3 h-3" /> Portal Asesores (mis ventas)
              </a>
            </div>
          )}
        </div>
      </section>
      <section id="how" className="py-24 border-t border-neutral-200 bg-white">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-14">
            <p className="text-sm font-bold uppercase tracking-widest text-neutral-500 mb-3">Cómo funciona</p>
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight">
              {isB2C
                ? "GPS + IA en 3 pasos. Sin descargas."
                : "Cómo llega tu promoción al celular de cada transeúnte."}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {(isB2C
              ? [
                  { n: "01", icon: QrCode, title: "Escanea el QR", desc: "Cero registros. Tu celular se convierte en un radar de ofertas al instante.", color: "text-[#FF007A]" },
                  { n: "02", icon: MapPin, title: "Camina cerca de un comercio", desc: "Cuando pases a menos de 50 metros de un local afiliado, recibes su oferta activa.", color: "text-[#0055FF]" },
                  { n: "03", icon: MessageSquare, title: "Chatea con la IA", desc: "Toca 'Quiero saber más' y la IA del comercio te explica la promo y sugiere otros productos.", color: "text-emerald-500" },
                ]
              : [
                  { n: "01", icon: QrCode, title: "Las personas escanean el QR", desc: "Los transeúntes escanean el código QR (impreso en la vitrina de los negocios participantes — en plazas, centros comerciales, calles y avenidas — o en la red HOLA RADAR) para recibir mensajes de su comercio. Sin registros, sin fricción.", color: "text-[#FF007A]" },
                  { n: "02", icon: MapPin, title: "Su comercio los detecta a 50m", desc: "El celular de esas personas se convierte en un radar. Cuando caminen cerca de su comercio, nuestra IA les envía su mensaje con la promoción diseñada por usted.", color: "text-[#0055FF]" },
                  { n: "03", icon: MessageSquare, title: "La IA los convence de entrar", desc: "Cuando el cliente toca 'Saber más', la IA vendedora de su comercio conversa con él, resuelve dudas y lo invita a entrar a comprar en ese instante — 24/7, en español.", color: "text-emerald-500" },
                ]
            ).map((s) => (
              <div key={s.n} className="p-8 rounded-2xl border border-neutral-200 bg-[#FAFAFA] hover:shadow-md transition-shadow">
                <div className={`text-6xl font-heading font-black ${s.color} leading-none mb-6`}>{s.n}</div>
                <s.icon className="w-6 h-6 mb-3 text-neutral-700" />
                <h3 className="font-heading font-bold text-xl mb-2">{s.title}</h3>
                <p className="text-neutral-600 leading-relaxed text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section id="benefits" className="py-24">
        <div className="max-w-6xl mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-10">
          <div className="p-10 rounded-3xl bg-gradient-to-br from-[#FF007A] to-[#ff4d9e] text-white">
            <Sparkles className="w-8 h-8 mb-4" />
            <h3 className="font-heading text-3xl font-extrabold mb-4">Para ti, que caminas la ciudad</h3>
            <ul className="space-y-3 text-white/90">
              <li className="flex gap-2"><Gift className="w-5 h-5 shrink-0 mt-0.5" /> Regalos y descuentos exclusivos por pasar cerca.</li>
              <li className="flex gap-2"><Radar className="w-5 h-5 shrink-0 mt-0.5" /> Radar GPS con control total: encendido/apagado en un tap.</li>
              <li className="flex gap-2"><MessageSquare className="w-5 h-5 shrink-0 mt-0.5" /> IA que resuelve dudas en tu idioma, al instante.</li>
            </ul>
            <Link to="/scan" className="inline-block mt-6">
              <Button className="rounded-full bg-white text-[#FF007A] hover:bg-white/90 font-bold" data-testid="benefits-b2c-cta">
                Ver el radar en vivo <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          <div className="p-10 rounded-3xl bg-gradient-to-br from-[#0055FF] to-[#3d7bff] text-white">
            <BarChart3 className="w-8 h-8 mb-4" />
            <h3 className="font-heading text-3xl font-extrabold mb-4">Para tu comercio</h3>
            <ul className="space-y-3 text-white/90">
              <li className="flex gap-2"><MapPin className="w-5 h-5 shrink-0 mt-0.5" /> Alcance hiperlocal: solo pagas por transeúntes reales.</li>
              <li className="flex gap-2"><Zap className="w-5 h-5 shrink-0 mt-0.5" /> IA vendedora que responde 24/7 en cada match.</li>
              <li className="flex gap-2"><BarChart3 className="w-5 h-5 shrink-0 mt-0.5" /> Dashboard con matches, conversiones y ofertas.</li>
            </ul>
            <Link to="/register" className="inline-block mt-6">
              <Button className="rounded-full bg-white text-[#0055FF] hover:bg-white/90 font-bold" data-testid="benefits-b2b-cta">
                Registrar mi comercio <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing — solo visible en modo Comercios */}
      {!isB2C && (
      <section id="pricing" className="py-24 bg-black text-white">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-sm font-bold uppercase tracking-widest text-[#00FF7F] mb-3">Precios para comercios</p>
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight">
              Elige el plan que va con tu negocio.
            </h2>
            <p className="mt-4 text-neutral-400">
              Dos planes prepago. Sin renovación automática. Cancela cuando quieras.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 items-stretch" data-testid="pricing-cards-row">
            {/* JUNIOR CARD */}
            <div className="rounded-3xl bg-gradient-to-br from-[#FF007A] to-[#FF6B00] p-6 md:p-8 shadow-2xl shadow-[#FF007A]/25 relative overflow-hidden flex flex-col" data-testid="pricing-card-cupones">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10 blur-3xl" />
              <div className="relative flex-1 flex flex-col">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/20 text-white px-3 py-1 text-xs font-bold uppercase tracking-widest mb-5 self-start">
                  <Store className="w-3.5 h-3.5" /> Paquete Cupones Marketing
                </div>
                <div className="text-[11px] uppercase tracking-widest font-bold text-white/80 mb-2">
                  Tu base de clientes, cupones con diseño IA y boletos del sorteo — sin pagar publicidad
                </div>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="font-heading font-black text-5xl md:text-6xl leading-none">$299</span>
                  <span className="text-white/85 font-semibold">MXN</span>
                </div>
                <div className="mt-1 text-sm text-white/85 font-medium">+ IVA · cada 30 días</div>
                <div className="mt-1 text-xs text-white/70">
                  Total con IVA: <strong>$346.84 MXN</strong> · o $18 USD via tarjeta.
                </div>
                <ul className="mt-6 space-y-2.5 text-sm flex-1">
                  {[
                    { key: "cup-qr", node: <><strong>QR personalizado</strong> de tu negocio + póster imprimible</> },
                    { key: "cup-db", node: "Base de datos propia: cada escaneo es un cliente tuyo" },
                    { key: "cup-ai", node: <><strong>Cupones diseñados con IA</strong> (6 al mes)</> },
                    { key: "cup-camp", node: "Campañas por segmento con el nombre del cliente" },
                    { key: "cup-city", node: <><strong>Campañas a toda tu población</strong> — llega a todos los usuarios HOLA RADAR de tu ciudad, aunque no sean tu base</> },
                    { key: "cup-raffle", node: <><strong>Entrega boletos del Sorteo Mensual</strong> de electrodomésticos en cada compra (premios pagados por nosotros)</> },
                    { key: "cup-push", node: "Avisos push y recordatorio de vencimiento" },
                    { key: "cup-redeem", node: "Canje en caja con medición de ventas y conversión" },
                    { key: "cup-gold", node: "Cuponera del cliente + concurso CUPÓN DE ORO" },
                    { key: "cup-payments", node: "OXXO, tarjeta y SPEI — activación instantánea" },
                  ].map((f) => (
                    <li key={f.key} className="flex items-start gap-2">
                      <Check className="w-4 h-4 shrink-0 mt-0.5 text-white" />
                      <span>{f.node}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register" className="inline-block mt-7">
                  <Button className="w-full sm:w-auto rounded-full bg-white text-[#FF007A] hover:bg-white/95 font-bold h-12 px-6 shadow-xl" data-testid="pricing-cupones-cta">
                    Registra tu negocio <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <div className="mt-2 text-xs text-white/80">Sin contrato forzoso. Cancela cuando quieras.</div>
              </div>
            </div>

            {/* PRO CARD */}
            <div className="rounded-3xl bg-gradient-to-br from-[#0055FF] to-[#3d7bff] p-6 md:p-8 shadow-2xl shadow-[#0055FF]/30 relative overflow-hidden flex flex-col" data-testid="pricing-card-pro">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10 blur-3xl" />
              <div className="absolute top-4 right-4 rounded-full bg-[#00FF7F] text-black text-[10px] font-black uppercase tracking-widest px-2.5 py-1">
                Más popular
              </div>
              <div className="relative flex-1 flex flex-col">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/20 text-white px-3 py-1 text-xs font-bold uppercase tracking-widest mb-5 self-start">
                  <Store className="w-3.5 h-3.5" /> Paquete HOLA RADAR (todo incluido)
                </div>
                <div className="text-[11px] uppercase tracking-widest font-bold text-white/80 mb-2">
                  Toda la plataforma: radar GPS, Aura IA, ofertas, gamificación y el Paquete Cupones incluido
                </div>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="font-heading font-black text-5xl md:text-6xl leading-none">$990</span>
                  <span className="text-white/85 font-semibold">MXN</span>
                </div>
                <div className="mt-1 text-sm text-white/85 font-medium">+ IVA · cada 30 días</div>
                <div className="mt-1 text-xs text-white/70">
                  Total con IVA: <strong>$1,148.40 MXN</strong> · o $59 USD via tarjeta.
                </div>
                <ul className="mt-6 space-y-2.5 text-sm flex-1">
                  {[
                    { key: "pro-radar", node: "Radar GPS activo — Vitrina 50m / Plaza 200m / Vial 500m" },
                    { key: "pro-referrals", node: <><strong>Sistema de Ventas Referidas</strong>: galería con comisión, promotores vendiendo desde casa y registro de ventas</> },
                    { key: "pro-matches", node: <><strong>Hasta 1,400 matches</strong> al mes</> },
                    { key: "pro-offers", node: "Ofertas y regalos ilimitados" },
                    { key: "pro-designs", node: <><strong>8 diseños IA</strong> de mensaje al mes</> },
                    { key: "pro-chat", node: "Chat IA vendedor 24/7 en cada match" },
                    { key: "pro-dashboard", node: "Dashboard con matches, conversiones y ventas" },
                    { key: "pro-qr", node: "QR único y póster imprimible para vitrina" },
                    { key: "pro-payments", node: "OXXO, tarjeta y SPEI — activación instantánea" },
                  ].map((f) => (
                    <li key={f.key} className="flex items-start gap-2">
                      <Check className="w-4 h-4 shrink-0 mt-0.5 text-[#00FF7F]" />
                      <span>{f.node}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register" className="inline-block mt-7">
                  <Button className="w-full sm:w-auto rounded-full bg-white text-[#0055FF] hover:bg-white/95 font-bold h-12 px-6 shadow-xl" data-testid="pricing-pro-cta">
                    Registra tu negocio <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <div className="mt-2 text-xs text-white/80">Sin contrato forzoso. Cancela cuando quieras.</div>
              </div>
            </div>
          </div>

          {/* Info strip abajo */}
          <div className="mt-10 rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:p-8 grid md:grid-cols-3 gap-6">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2">Cómo funciona el cobro</div>
              <div className="space-y-1 text-sm text-neutral-300 leading-relaxed">
                <div className="flex gap-2"><span className="text-[#00FF7F] font-black">1.</span> Regístrate y elige tu plan (Pequeño o Grande).</div>
                <div className="flex gap-2"><span className="text-[#00FF7F] font-black">2.</span> Paga tu plan por tarjeta, OXXO o SPEI — activación instantánea.</div>
                <div className="flex gap-2"><span className="text-[#00FF7F] font-black">3.</span> Radar activo 30 días. Si no renuevas, se pausa. Cero cargos automáticos, sin contrato forzoso.</div>
              </div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2">Métodos de pago</div>
              <div className="flex flex-wrap gap-1.5 text-xs">
                {["Visa/MC", "OXXO", "SPEI BBVA", "Stripe USD"].map((m) => (
                  <span key={m} className="rounded-full bg-white/10 px-2.5 py-1 text-white/90 font-semibold">{m}</span>
                ))}
              </div>
              <div className="mt-3 text-xs text-neutral-400 leading-relaxed">
                Facturación CFDI disponible. Escribe a <span className="text-white">facturacion@holaradar.mx</span>.
              </div>
            </div>
            <div className="flex gap-3 items-start">
              <Sparkles className="w-5 h-5 text-[#00FF7F] shrink-0 mt-0.5" />
              <div className="text-sm text-neutral-300 leading-relaxed">
                <strong className="text-white">Diseño IA incluido</strong> en ambos planes — 4 (Junior) u 8 (Pro) rediseños al mes con Nano Banana + IA. Estilo colorido, sobrio, juvenil o elegante.
              </div>
            </div>
          </div>

          {/* Bottom CTA row */}
          <div className="mt-14 flex flex-wrap gap-3 justify-center">
            <Link to="/register">
              <Button className="h-14 px-8 rounded-full bg-[#0055FF] hover:bg-[#0044cc] font-bold" data-testid="footer-cta-merchant">
                Soy comercio · Empezar
              </Button>
            </Link>
            <Link to="/scan">
              <Button className="h-14 px-8 rounded-full bg-[#FF007A] hover:bg-[#e5006e] font-bold" data-testid="footer-cta-user">
                Ver el radar en vivo
              </Button>
            </Link>
          </div>
        </div>
      </section>
      )}

      <footer className="py-10 border-t border-neutral-200 bg-white">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="grid md:grid-cols-3 gap-8 text-sm">
            <div>
              <div className="font-heading font-extrabold text-lg mb-2">HOLA RADAR</div>
              <div className="text-xs text-neutral-500 leading-relaxed">
                <div className="font-bold text-neutral-700">Marketing de Inteligencia Artificial S de RL de CV</div>
                <div className="mt-1">Av. Américas 1545, pisos 20, 21 y 22, Col. Providencia, Guadalajara, Jalisco, México.</div>
                <div className="mt-2">Representante legal: <strong className="text-neutral-700">Ricardo Zermeño Mejía</strong></div>
              </div>
            </div>
            <div>
              <div className="font-heading font-extrabold text-sm uppercase tracking-widest text-neutral-500 mb-3">Legal</div>
              <ul className="space-y-1.5">
                <li><Link to="/legal/privacy" className="text-neutral-600 hover:text-black" data-testid="footer-privacy">Aviso de Privacidad</Link></li>
                <li><Link to="/legal/terms-users" className="text-neutral-600 hover:text-black" data-testid="footer-terms-users">Términos · Usuarios</Link></li>
                <li><Link to="/legal/terms-merchants" className="text-neutral-600 hover:text-black" data-testid="footer-terms-merchants">Términos · Comercios</Link></li>
                <li><Link to="/legal/nda" className="text-neutral-600 hover:text-black" data-testid="footer-nda">Acuerdo de Confidencialidad</Link></li>
              </ul>
            </div>
            <div>
              <div className="font-heading font-extrabold text-sm uppercase tracking-widest text-neutral-500 mb-3">Accesos</div>
              <ul className="space-y-1.5">
                <li><Link to="/login" className="text-neutral-600 hover:text-black">Entrar comercio</Link></li>
                <li><Link to="/register" className="text-neutral-600 hover:text-black">Registrar comercio</Link></li>
                <li><Link to="/scan" className="text-neutral-600 hover:text-black">Ver ejemplo del radar</Link></li>
                <li><Link to="/admin/login" className="text-neutral-500 hover:text-black inline-flex items-center gap-1.5" data-testid="footer-admin-link"><span className="w-1.5 h-1.5 rounded-full bg-[#FF007A]"></span>Acceso Admin</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-neutral-200 text-center text-xs text-neutral-500">
            © 2026 HOLA RADAR · Hecho con GPS + IA · Guadalajara, MX
          </div>
        </div>
      </footer>
    </div>
  );
}

function PhoneMockup() {
  return (
    <div className="relative">
      <div className="w-[300px] h-[600px] rounded-[42px] bg-neutral-900 p-3 shadow-2xl">
        <div className="w-full h-full rounded-[32px] bg-gradient-to-b from-white to-neutral-50 overflow-hidden relative">
          {/* Notch */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 rounded-full bg-neutral-900 z-10"></div>
          {/* Content */}
          <div className="pt-12 px-5">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-black text-white grid place-items-center">
                  <Radar className="w-4 h-4" />
                </div>
                <span className="font-heading font-extrabold text-sm">HOLA RADAR</span>
              </div>
              <div className="w-3 h-3 rounded-full bg-[#00FF7F] shadow-[0_0_10px_#00FF7F]"></div>
            </div>

            {/* Radar */}
            <div className="relative h-40 grid place-items-center mb-5">
              <div className="absolute inset-0 grid place-items-center">
                <div className="absolute w-32 h-32 rounded-full border border-[#FF007A]/40 radar-ping"></div>
                <div className="absolute w-32 h-32 rounded-full border border-[#FF007A]/40 radar-ping radar-ping-delay-1"></div>
                <div className="absolute w-32 h-32 rounded-full border border-[#FF007A]/40 radar-ping radar-ping-delay-2"></div>
              </div>
              <div className="w-14 h-14 rounded-full bg-[#FF007A] text-white grid place-items-center shadow-lg shadow-[#FF007A]/50 relative z-10">
                <Radar className="w-6 h-6" />
              </div>
            </div>

            {/* Match card */}
            <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm p-4">
              <div className="flex items-center gap-2 text-xs font-bold text-[#FF007A] mb-2">
                <Gift className="w-3.5 h-3.5" /> MATCH · 32m
              </div>
              <div className="font-heading font-extrabold text-lg leading-tight">
                Café Doble 2×1
              </div>
              <div className="text-xs text-neutral-500 mt-1">Cafetería Origen · Hoy</div>
              <div className="mt-3 h-9 rounded-full bg-black text-white grid place-items-center text-xs font-bold">
                Quiero saber más
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MapMockup() {
  return (
    <div className="relative w-[420px] max-w-full">
      <div className="rounded-3xl border border-neutral-200 bg-white shadow-xl overflow-hidden">
        {/* Fake map */}
        <div className="relative h-80 bg-[#EAEEF3]">
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 320" preserveAspectRatio="none">
            <path d="M0 80 L400 100" stroke="#D0D6DE" strokeWidth="18" />
            <path d="M0 200 L400 180" stroke="#D0D6DE" strokeWidth="14" />
            <path d="M120 0 L140 320" stroke="#D0D6DE" strokeWidth="16" />
            <path d="M280 0 L260 320" stroke="#D0D6DE" strokeWidth="10" />
            <rect x="30" y="20" width="60" height="40" fill="#DFE4EB" rx="4" />
            <rect x="300" y="30" width="70" height="30" fill="#DFE4EB" rx="4" />
            <rect x="50" y="230" width="50" height="60" fill="#DFE4EB" rx="4" />
            <rect x="300" y="220" width="70" height="70" fill="#DFE4EB" rx="4" />
          </svg>
          {/* Radar radius */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="relative">
              <div className="w-40 h-40 rounded-full bg-[#0055FF]/15 border-2 border-[#0055FF]/40"></div>
              <div className="absolute inset-0 grid place-items-center">
                <div className="w-10 h-10 rounded-full bg-[#0055FF] text-white grid place-items-center shadow-lg shadow-[#0055FF]/40">
                  <Store className="w-5 h-5" />
                </div>
              </div>
              {/* dots (people) */}
              <div className="absolute -top-2 left-6 w-2.5 h-2.5 rounded-full bg-[#FF007A] animate-pulse"></div>
              <div className="absolute top-10 -right-2 w-2.5 h-2.5 rounded-full bg-[#FF007A] animate-pulse"></div>
              <div className="absolute bottom-2 left-4 w-2.5 h-2.5 rounded-full bg-[#FF007A] animate-pulse"></div>
            </div>
          </div>
          <div className="absolute top-4 left-4 rounded-full bg-white shadow px-3 py-1 text-xs font-bold text-[#0055FF]">
            Radio activo · 50m
          </div>
        </div>
        {/* Dashboard preview */}
        <div className="p-5">
          <div className="grid grid-cols-3 gap-3">
            <MiniStat label="Matches" value="42" />
            <MiniStat label="Interesados" value="18" />
            <MiniStat label="Ventas IA" value="7" />
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl bg-[#FAFAFA] border border-neutral-200 p-3">
      <div className="text-xs text-neutral-500 font-medium">{label}</div>
      <div className="font-heading font-extrabold text-2xl mt-0.5">{value}</div>
    </div>
  );
}

function AIMessageMockup() {
  return (
    <div className="relative w-[320px] sm:w-[360px] max-w-full">
      {/* AI badge floating */}
      <div className="absolute -top-4 -left-4 z-20 rounded-full bg-white border border-[#0055FF]/30 shadow-xl px-3 py-1.5 flex items-center gap-1.5 text-xs font-bold text-[#0055FF]">
        <Sparkles className="w-3.5 h-3.5" /> Diseñado por IA en 2.4s
      </div>
      {/* "Delivered" badge */}
      <div className="absolute -top-3 right-4 z-20 rounded-full bg-[#00FF7F] shadow-lg px-3 py-1 text-[10px] font-black text-black uppercase tracking-widest">
        Entregado
      </div>

      {/* Phone frame */}
      <div className="rounded-[42px] bg-neutral-900 p-3 shadow-2xl">
        <div className="rounded-[32px] bg-white overflow-hidden relative">
          {/* Notch */}
          <div className="pt-8 pb-3 px-5 relative">
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 rounded-full bg-neutral-900"></div>
            <div className="flex items-center justify-between text-[10px] text-neutral-400 font-semibold">
              <span>9:41</span>
              <span>HOLA RADAR · a 22m de ti</span>
            </div>
          </div>

          {/* AI generated message card — this is the actual output the merchant AI generator produces */}
          <div className="px-4 pb-5">
            <div
              className="rounded-3xl p-5 text-white shadow-2xl relative overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #FF007A 0%, #FF6B00 55%, #FFB800 100%)",
              }}
            >
              {/* Grain/shine */}
              <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle at 20% 10%, rgba(255,255,255,0.4), transparent 40%)" }} />

              {/* Header */}
              <div className="relative flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl bg-white/25 backdrop-blur grid place-items-center text-lg">
                    ✨
                  </div>
                  <div className="text-[11px] uppercase font-black tracking-widest opacity-90">
                    Boutique Aria
                  </div>
                </div>
                <div className="rounded-full bg-black text-[#FFB800] text-[10px] font-black uppercase tracking-widest px-2.5 py-1">
                  Solo hoy
                </div>
              </div>

              {/* Headline */}
              <div className="relative font-heading font-black text-[26px] leading-[1.05] tracking-tight">
                20% ADICIONAL
              </div>
              <div className="relative font-heading font-black text-[20px] leading-tight opacity-95 mt-0.5">
                solo a ti que estás aquí
              </div>

              {/* Subhead */}
              <div className="relative text-[13px] leading-snug mt-3 opacity-95">
                A ti que <strong>recibiste este mensaje</strong>: 20% extra en compras mayores a <strong>$1,000 MXN</strong>. No lo verás en la vitrina — solo lo tienes tú.
              </div>

              {/* Distance pill */}
              <div className="relative flex items-center gap-1.5 mt-3 text-[11px] font-bold opacity-90">
                <MapPin className="w-3 h-3" /> A 22 metros · caduca en 12 min
              </div>

              {/* CTAs */}
              <div className="relative mt-4 grid grid-cols-[1fr_auto] gap-2">
                <div className="rounded-full bg-black text-white grid place-items-center font-bold text-sm h-11">
                  QUIERO IR AHORA →
                </div>
                <div className="rounded-full bg-white/25 text-white grid place-items-center font-bold text-sm h-11 px-4">
                  Ver más
                </div>
              </div>
            </div>

            {/* Below: AI meta strip */}
            <div className="mt-4 flex items-center gap-2 rounded-2xl bg-neutral-100 border border-neutral-200 p-3">
              <div className="w-8 h-8 rounded-xl bg-[#0055FF] text-white grid place-items-center shrink-0">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="text-[11px] leading-tight">
                <div className="font-black text-neutral-800">Generado por IA</div>
                <div className="text-neutral-500">Gradiente · copy · CTA · badge — en 2.4s</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricPill({ value, title, desc, icon: Icon }) {
  return (
    <div className="rounded-2xl bg-white border border-neutral-200 p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-[#0055FF]/10 text-[#0055FF] grid place-items-center">
          <Icon className="w-5 h-5" />
        </div>
        <div className="font-heading text-4xl font-black text-[#0055FF] leading-none tracking-tight">
          {value}
        </div>
      </div>
      <div className="font-heading font-extrabold text-lg tracking-tight">{title}</div>
      <p className="mt-1.5 text-sm text-neutral-600 leading-relaxed">{desc}</p>
    </div>
  );
}

